import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns';
import { z } from 'zod';

import { zonedEvent, zonedWallTimeToUtc } from '@/lib/timezone';

import type { CreateBlockedTimeInput } from '../api/types';

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose a date');

/**
 * A closure is one whole-day block from the first day to the last, for the
 * whole team. Not a repeating block: it reopens by itself, one delete removes
 * it, and Claire and the booking page read it the same way as any block. The
 * assistant's "we're closed until the 2nd" writes the same row.
 */
export const closureFormSchema = z
  .object({
    firstDay: day,
    lastDay: day,
    reason: z.string().trim().min(1, 'Add a reason').max(200),
  })
  .refine((values) => values.lastDay >= values.firstDay, {
    message: 'The last day cannot be before the first day',
    path: ['lastDay'],
  });

export type ClosureFormData = z.infer<typeof closureFormSchema>;

export function closureDefaults(startDate?: Date): ClosureFormData {
  const first = format(startDate ?? new Date(), 'yyyy-MM-dd');
  return { firstDay: first, lastDay: first, reason: 'Closed' };
}

interface ClosureRecord {
  title: string;
  startDate: string | Date;
  endDate: string | Date;
}

/** Is this calendar block a closure, rather than time blocked within a day? */
export function isClosure(block: {
  allDay?: boolean;
  rrule?: string | null;
  practitionerIds?: string[];
}): boolean {
  return (
    Boolean(block.allDay) &&
    !block.rrule &&
    (block.practitionerIds?.length ?? 0) === 0
  );
}

/** The saved block back as form values. It ends at midnight after the last day. */
export function closureFormValues(
  closure: ClosureRecord,
  timeZone: string
): ClosureFormData {
  const day = (value: string | Date) =>
    format(zonedEvent(new Date(value).toISOString(), timeZone), 'yyyy-MM-dd');
  return {
    firstDay: day(closure.startDate),
    lastDay: format(addDays(parseISO(day(closure.endDate)), -1), 'yyyy-MM-dd'),
    reason: closure.title,
  };
}

/** Whole days closed, counting both ends. */
export function closureDayCount(values: {
  firstDay: string;
  lastDay: string;
}): number {
  return (
    differenceInCalendarDays(
      parseISO(values.lastDay),
      parseISO(values.firstDay)
    ) + 1
  );
}

/** Midnight to midnight in the BUSINESS timezone, so a closure is whole days there. */
export function buildClosurePayload(
  values: ClosureFormData,
  timeZone: string
): CreateBlockedTimeInput {
  const dayAfter = addDays(parseISO(values.lastDay), 1);
  return {
    title: values.reason,
    startDate: zonedWallTimeToUtc(parseISO(values.firstDay), 0, 0, timeZone),
    endDate: zonedWallTimeToUtc(dayAfter, 0, 0, timeZone),
    allDay: true,
    timezone: timeZone,
    practitionerIds: [],
  };
}
